import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { blogPosts } from '../data/blog';

const ALL_CATS = ['All', ...new Set(blogPosts.map(p => p.category))];

const BLOG_IMAGES = {
  'Web Development': 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=600&q=80',
  'SEO': 'https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a?w=600&q=80',
  'App Development': 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=600&q=80',
  'Digital Marketing': 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=600&q=80',
  'Web Design': 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=600&q=80',
};

export default function Blog() {
  useScrollReveal();
  const [active, setActive] = useState('All');
  const filtered = active === 'All' ? blogPosts : blogPosts.filter(p => p.category === active);
  const [featured, ...rest] = filtered;

  return (
    <>
      <SEO title="Blog — Web Development, SEO, Design & Marketing Insights" description="Practical insights on web development, SEO, app development, web design, and digital marketing from the CodeViora team." canonical="/blog" />

      {/* Hero */}
      <section className="bg-grid-black pt-24 pb-16 md:pt-32 md:pb-20 relative overflow-hidden">
        <div className="container-max relative">
          <div className="max-w-2xl animate-fade-up">
            <span className="section-label-inv">Blog</span>
            <h1 className="section-title-inv text-5xl md:text-6xl mt-2">INSIGHTS ON DIGITAL GROWTH.</h1>
            <p className="section-sub-inv">Practical articles on web development, SEO, design, and digital marketing — written by people who do this work every day.</p>
          </div>
        </div>
      </section>

      {/* Filter */}
      <div className="bg-pure-black border-b border-white/10 sticky top-16 z-20">
        <div className="container-max">
          <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-none">
            {ALL_CATS.map(cat => (
              <button key={cat} onClick={() => setActive(cat)}
                className={`flex-shrink-0 px-4 py-2 rounded-lg text-xs font-bold transition-colors tracking-wide ${active === cat ? 'bg-white text-black' : 'text-white/40 hover:text-white hover:bg-white/5'}`}
                aria-pressed={active === cat}>
                {cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      <section className="bg-grid-white py-16 md:py-20">
        <div className="container-max">
          {filtered.length === 0 && <p className="text-black/30 text-center py-20">No posts in this category yet.</p>}

          {/* Featured */}
          {featured && (
            <Link to={`/blog/${featured.slug}`} className="reveal card-white group flex flex-col md:flex-row overflow-hidden mb-10">
              <div className="md:w-2/5 h-52 md:h-auto flex-shrink-0 overflow-hidden rounded-t-2xl md:rounded-l-2xl md:rounded-tr-none">
                <img src={BLOG_IMAGES[featured.category] || BLOG_IMAGES['Web Development']} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              </div>
              <div className="p-8 flex flex-col justify-center flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xs font-bold text-black bg-black/5 border border-black/10 px-2.5 py-1 rounded-full">{featured.category}</span>
                  <span className="text-xs text-black/30">{featured.readTime}</span>
                </div>
                <h2 className="text-xl md:text-2xl text-black leading-snug mb-3 group-hover:text-black/50 transition-colors" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>{featured.title}</h2>
                <p className="text-sm text-black/50 leading-relaxed mb-5">{featured.excerpt}</p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-black flex items-center justify-center text-white text-xs font-black">CV</div>
                  <div>
                    <p className="text-xs font-semibold text-black">{featured.author.name}</p>
                    <p className="text-xs text-black/30">{featured.date}</p>
                  </div>
                </div>
              </div>
            </Link>
          )}

          {/* Grid */}
          {rest.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map(({ slug, title, excerpt, category, date, readTime, author }, i) => (
                <Link key={slug} to={`/blog/${slug}`} className="reveal card-white group overflow-hidden flex flex-col" style={{transitionDelay:`${i*60}ms`}}>
                  <div className="h-44 overflow-hidden rounded-t-2xl">
                    <img src={BLOG_IMAGES[category] || BLOG_IMAGES['Web Development']} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs font-bold text-black bg-black/5 border border-black/10 px-2.5 py-1 rounded-full">{category}</span>
                      <span className="text-xs text-black/25">{readTime}</span>
                    </div>
                    <h2 className="font-bold text-black leading-snug mb-2 group-hover:text-black/50 transition-colors flex-1 line-clamp-2" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>{title}</h2>
                    <p className="text-sm text-black/50 leading-relaxed mb-4 line-clamp-2">{excerpt}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-black flex items-center justify-center text-white text-xs font-black">CV</div>
                        <p className="text-xs font-medium text-black/60">{author.name}</p>
                      </div>
                      <p className="text-xs text-black/25">{date}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter */}
      <section className="bg-pure-black border-t border-white/10 py-16">
        <div className="container-max max-w-xl text-center reveal">
          <h2 className="text-3xl text-white mb-3" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>GET NEW POSTS BY EMAIL.</h2>
          <p className="text-white/40 text-sm mb-6">No spam. Only when we publish something actually useful.</p>
          <form className="flex gap-3" onSubmit={e => { e.preventDefault(); alert('Subscribed! Thank you.'); }}>
            <input type="email" required placeholder="your@email.com" aria-label="Email address"
              className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-white/40" />
            <button type="submit" className="btn-primary-inv text-sm py-3 px-5 whitespace-nowrap">Subscribe</button>
          </form>
        </div>
      </section>
    </>
  );
}
